import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { publicApi, ApiError } from '../lib/api.ts';
import { Btn } from '../components/Btn.tsx';
import { CenteredSpinner } from '../components/Spinner.tsx';
import { Field, Select, TextInput } from '../components/Field.tsx';
import { ErrorMessage } from '../components/ErrorMessage.tsx';
import { VisitorShell } from './VisitorShell.tsx';

type VisitorQuestion = {
  id: string;
  label: string;
  type: string;
  help_text: string | null;
  options: string[] | null;
  required: boolean;
};

export function VisitorRegister() {
  const navigate = useNavigate();
  const { data, isLoading } = useQuery({
    queryKey: ['visitor-questions'],
    queryFn: () => publicApi.get<{ questions: VisitorQuestion[] }>('/api/visitor/questions'),
  });
  const { data: hostsData } = useQuery({
    queryKey: ['visitor-hosts'],
    queryFn: () => publicApi.get<{ hosts: { id: string; name: string }[] }>('/api/visitor/hosts'),
  });
  const hosts = hostsData?.hosts ?? [];

  const [form, setForm] = useState({
    name: '',
    phone: '',
    email: '',
    company_name: '',
    host_name: '',
    host_user_id: '' as string,
    purpose: '',
    site: '',
    proof_method: 'label' as 'label' | 'sms',
  });
  // '' = not chosen, a user id = picked staff, '__other__' = type a name.
  const [hostChoice, setHostChoice] = useState('');
  const [answers, setAnswers] = useState<Record<string, unknown>>({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);

  if (isLoading) return <VisitorShell><CenteredSpinner /></VisitorShell>;
  const questions = data?.questions ?? [];

  function setAnswer(id: string, value: unknown) {
    setAnswers((a) => ({ ...a, [id]: value }));
  }

  async function submit() {
    setError(null);
    setBusy(true);
    try {
      const res = await publicApi.post<{ visitor: { id: string } }>('/api/visitor/register', {
        name: form.name.trim(),
        phone: form.phone.trim() || null,
        email: form.email.trim() || null,
        company_name: form.company_name.trim() || null,
        host_name: form.host_name.trim(),
        host_user_id: form.host_user_id || null,
        purpose: form.purpose.trim() || null,
        site: form.site.trim() || null,
        proof_method: form.proof_method,
        answers: questions
          .filter((q) => answers[q.id] !== undefined && answers[q.id] !== '')
          .map((q) => ({ question_id: q.id, answer: answers[q.id] as never })),
      });
      navigate(`/visit/${res.visitor.id}`);
    } catch (err) {
      setError(err instanceof ApiError ? err : err);
      setBusy(false);
    }
  }

  const canSubmit =
    form.name.trim() && form.host_name.trim() && (form.proof_method !== 'sms' || form.phone.trim());

  return (
    <VisitorShell>
      <h1 className="text-xl font-semibold text-slate-900">Sign in to site</h1>
      <p className="mt-1 text-sm text-slate-500">
        Welcome. Please register your visit. You&apos;ll get proof of registration to show on site.
      </p>

      <div className="mt-5 space-y-4">
        <Field label="Full name" required>
          <TextInput value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
        </Field>
        {hosts.length > 0 && (
          <Field label="Who are you here to see?" required hint="Pick the person you're visiting.">
            <Select
              value={hostChoice}
              onChange={(e) => {
                const v = e.target.value;
                setHostChoice(v);
                if (v === '__other__') {
                  setForm((f) => ({ ...f, host_user_id: '', host_name: '' }));
                } else if (v === '') {
                  setForm((f) => ({ ...f, host_user_id: '', host_name: '' }));
                } else {
                  const host = hosts.find((h) => h.id === v);
                  setForm((f) => ({ ...f, host_user_id: v, host_name: host?.name ?? '' }));
                }
              }}
            >
              <option value="">Select…</option>
              {hosts.map((h) => (
                <option key={h.id} value={h.id}>{h.name}</option>
              ))}
              <option value="__other__">Someone else…</option>
            </Select>
          </Field>
        )}
        {(hosts.length === 0 || hostChoice === '__other__') && (
          <Field
            label={hosts.length === 0 ? 'Who are you here to see?' : 'Their name'}
            required
            hint="The person or team you're visiting."
          >
            <TextInput
              value={form.host_name}
              placeholder="e.g. Jordan Lee, Warehouse team"
              onChange={(e) => setForm({ ...form, host_name: e.target.value, host_user_id: '' })}
            />
          </Field>
        )}
        <div className="grid grid-cols-2 gap-3">
          <Field label="Phone">
            <TextInput value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
          </Field>
          <Field label="Email">
            <TextInput type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
          </Field>
          <Field label="Visiting from (company)">
            <TextInput value={form.company_name} onChange={(e) => setForm({ ...form, company_name: e.target.value })} />
          </Field>
        </div>
        <Field label="Purpose of visit">
          <TextInput value={form.purpose} onChange={(e) => setForm({ ...form, purpose: e.target.value })} />
        </Field>

        {questions.map((q) => (
          <Field key={q.id} label={q.label} hint={q.help_text ?? undefined}>
            {q.type === 'yesno' ? (
              <div className="flex gap-4 text-sm">
                {['Yes', 'No'].map((opt) => (
                  <label key={opt} className="inline-flex items-center gap-1.5">
                    <input
                      type="radio"
                      name={q.id}
                      checked={answers[q.id] === (opt === 'Yes')}
                      onChange={() => setAnswer(q.id, opt === 'Yes')}
                    />
                    {opt}
                  </label>
                ))}
              </div>
            ) : q.type === 'select' ? (
              <Select value={(answers[q.id] as string) ?? ''} onChange={(e) => setAnswer(q.id, e.target.value)}>
                <option value="">Select…</option>
                {(q.options ?? []).map((o) => (
                  <option key={o} value={o}>{o}</option>
                ))}
              </Select>
            ) : q.type === 'multiselect' ? (
              <div className="space-y-1">
                {(q.options ?? []).map((o) => {
                  const arr = (answers[q.id] as string[]) ?? [];
                  return (
                    <label key={o} className="flex items-center gap-2 text-sm">
                      <input
                        type="checkbox"
                        checked={arr.includes(o)}
                        onChange={(e) =>
                          setAnswer(q.id, e.target.checked ? [...arr, o] : arr.filter((x) => x !== o))
                        }
                      />
                      {o}
                    </label>
                  );
                })}
              </div>
            ) : (
              <TextInput value={(answers[q.id] as string) ?? ''} onChange={(e) => setAnswer(q.id, e.target.value)} />
            )}
          </Field>
        ))}

        <Field label="Proof of registration">
          <div className="flex gap-4 text-sm">
            <label className="inline-flex items-center gap-1.5">
              <input
                type="radio"
                checked={form.proof_method === 'label'}
                onChange={() => setForm({ ...form, proof_method: 'label' })}
              />
              Print a label
            </label>
            <label className="inline-flex items-center gap-1.5">
              <input
                type="radio"
                checked={form.proof_method === 'sms'}
                onChange={() => setForm({ ...form, proof_method: 'sms' })}
              />
              Text me (SMS)
            </label>
          </div>
        </Field>
        {form.proof_method === 'sms' && !form.phone.trim() && (
          <p className="text-xs text-amber-700">Enter a phone number to receive an SMS.</p>
        )}

        <Btn onClick={submit} loading={busy} disabled={!canSubmit}>
          Register my visit
        </Btn>
        <ErrorMessage error={error} />
      </div>
    </VisitorShell>
  );
}
